import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { fileUrl } from "@/lib/api";
import { ChevronLeft, ChevronRight, ArrowLeft, ArrowRight, Trash2 } from "lucide-react";

export default function ServicePhotoViewer({ photos, index, onIndex, onChange, readOnly = false }) {
  const open = index !== null && index >= 0 && index < photos.length;
  const photo = open ? photos[index] : null;
  const [note, setNote] = useState("");
  const touchX = useRef(null);

  useEffect(() => { setNote(photo?.note || ""); }, [photo?.id, photo?.note]);

  const go = (d) => {
    const n = index + d;
    if (n >= 0 && n < photos.length) onIndex(n);
  };
  const saveNote = () => {
    if (readOnly || !photo || (photo.note || "") === note) return;
    onChange(photos.map((p) => (p.id === photo.id ? { ...p, note } : p)));
  };
  const move = (d) => {
    const n = index + d;
    if (n < 0 || n >= photos.length) return;
    const next = [...photos];
    [next[index], next[n]] = [next[n], next[index]];
    onChange(next);
    onIndex(n);
  };
  const remove = () => {
    if (!window.confirm(`Delete photo ${index + 1}?`)) return;
    const next = photos.filter((p) => p.id !== photo.id);
    onChange(next);
    onIndex(next.length ? Math.min(index, next.length - 1) : null);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { saveNote(); onIndex(null); } }}>
      <DialogContent className="bg-[#0A0604] border-[#8B6A1F]/45 rounded-sm max-w-3xl w-[96vw] p-3 sm:p-4 text-[#F9F0D4]" data-testid="service-photo-viewer">
        <DialogTitle className="font-mono-data text-sm text-[#F9F0D4]" data-testid="service-photo-viewer-title">
          Photo {open ? index + 1 : 0} of {photos.length}
        </DialogTitle>
        <DialogDescription className="sr-only">Swipe or use arrows to step through the teardown photos.</DialogDescription>
        {photo && (
          <>
            <div
              className="relative flex items-center justify-center bg-black/40 rounded-sm select-none"
              onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
              onTouchEnd={(e) => {
                if (touchX.current === null) return;
                const dx = e.changedTouches[0].clientX - touchX.current;
                touchX.current = null;
                if (Math.abs(dx) > 40) { saveNote(); go(dx < 0 ? 1 : -1); }
              }}
            >
              <img src={fileUrl(photo.path)} alt={`Photo ${index + 1}`} className="max-h-[62vh] w-auto max-w-full object-contain" data-testid="service-photo-viewer-img" />
              <NavBtn side="left" disabled={index === 0} onClick={() => { saveNote(); go(-1); }} testid="service-photo-prev"><ChevronLeft size={20} /></NavBtn>
              <NavBtn side="right" disabled={index === photos.length - 1} onClick={() => { saveNote(); go(1); }} testid="service-photo-next"><ChevronRight size={20} /></NavBtn>
            </div>
            {readOnly ? (
              photo.note ? <div className="text-sm text-[#F9F0D4] break-words" data-testid="service-photo-note-text">{photo.note}</div> : null
            ) : (<>
            <input
              value={note}
              onChange={(e) => setNote(e.target.value)}
              onBlur={saveNote}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); e.currentTarget.blur(); } }}
              placeholder="Add a note (optional) — e.g. keyless works removed"
              className="w-full px-3 h-10 bg-[#F9F0D4] text-[#0A0604] text-sm rounded-sm border border-[#8B6A1F]/45 focus:outline-none focus:border-[#D4AF37]"
              data-testid="service-photo-note"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex gap-1.5">
                <ToolBtn onClick={() => { saveNote(); move(-1); }} disabled={index === 0} testid="service-photo-move-earlier"><ArrowLeft size={12} /> Earlier</ToolBtn>
                <ToolBtn onClick={() => { saveNote(); move(1); }} disabled={index === photos.length - 1} testid="service-photo-move-later">Later <ArrowRight size={12} /></ToolBtn>
              </div>
              <ToolBtn onClick={remove} danger testid="service-photo-delete"><Trash2 size={12} /> Delete</ToolBtn>
            </div>
            </>)}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function NavBtn({ side, disabled, onClick, children, testid }) {
  if (disabled) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`absolute top-1/2 -translate-y-1/2 ${side === "left" ? "left-1" : "right-1"} w-10 h-10 flex items-center justify-center rounded-full bg-black/55 text-white hover:bg-black/75 transition-colors`}
      data-testid={testid}
      aria-label={side === "left" ? "Previous photo" : "Next photo"}
    >
      {children}
    </button>
  );
}

function ToolBtn({ children, onClick, disabled, danger, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex items-center gap-1 px-2.5 h-8 text-[10px] uppercase tracking-wider border rounded-sm disabled:opacity-40 transition-colors ${danger ? "border-rose-400/50 text-rose-300 hover:bg-rose-500/15" : "border-[#D4AF37]/40 text-[#F9F0D4] hover:bg-[#D4AF37]/15"}`}
      data-testid={testid}
    >
      {children}
    </button>
  );
}
